import "./SurveyTypeSelect.scss";

import { Flex, Text } from "@fluentui/react-northstar";
import { QuestionType, TeamsSurveyTypes } from "@teams/types";
import React from "react";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import { PollyIcon, PollyIconName } from "../../../_shared/components/PollyIcon";

type TypeSelectItemGroupType = "warmUp" | "engage" | "gatherFeedback";
type TypeSelectItemGroup = {
  label: string;
  items: TypeSelectItem[];
};

export type TypeSelectItem = {
  type: TeamsSurveyTypes;
  questionType?: QuestionType;
  questionTemplateId?: string;
  icon: PollyIconName;
  label: string;
};

export const SurveyTypeSelect: React.FC<{
  onSelected: (type: TypeSelectItem) => void;
}> = ({ onSelected }) => {
  const { user } = useUserInfo();

  let choiceGroup: Record<TypeSelectItemGroupType, TypeSelectItemGroup> = {
    warmUp: {
      label: "Warm up the meeting",
      items: [],
    },
    engage: {
      label: "Engage your audience",
      items: [],
    },
    gatherFeedback: {
      label: "Gather feedback",
      items: [],
    },
  };

  if (user.featureFlags.hotTake) {
    choiceGroup.warmUp.items.push({
      type: "hot-take",
      label: "Hot Take",
      icon: "hot-takes",
    });
  }

  if (user.featureFlags.icebreaker) {
    choiceGroup.warmUp.items.push({
      type: "icebreaker",
      label: "Icebreaker",
      icon: "icebreaker",
    });
  }

  if (user.featureFlags.qna) {
    choiceGroup.engage.items.push({
      type: "qna",
      label: "Q&A",
      icon: "qa-icon",
    });
  }

  if (user.featureFlags.trivia) {
    choiceGroup.engage.items.push({
      type: "trivia",
      label: "Trivia",
      icon: "lightbulb-icon",
    });
  }

  if (user.featureFlags.quiz) {
    choiceGroup.engage.items.push({
      type: "quiz",
      label: "Quiz",
      icon: "quiz",
    });
  }

  choiceGroup.gatherFeedback.items.push({
    type: "poll",
    questionType: "nominal",
    label: "Multiple Choice",
    icon: "multiple-choice-icon",
  });

  choiceGroup.gatherFeedback.items.push({
    type: "poll",
    questionType: "open-ended",
    label: "Open Text",
    icon: "message-icon",
  });

  choiceGroup.gatherFeedback.items.push({
    type: "poll",
    questionType: "ordinal",
    questionTemplateId: "qte:3a7b2a39-ee16-4672-b279-81f62c661c6f",
    label: "Rating",
    icon: "rating-icon",
  });

  return (
    <Flex column>
      <Text content="Create a polly" className="survey-type-select__header" align="center" />

      {Object.keys(choiceGroup).map(groupType => {
        const group = choiceGroup[groupType as TypeSelectItemGroupType];

        if (group.items.length === 0) {
          return null;
        }

        return (
          <Flex key={groupType} gap="gap.small" column>
            <Text content={group.label} className="survey-type-select__group-label" />
            <div className="survey-type-select__group">
              {group.items.map(choice => (
                <SelectItem key={choice.icon} choice={choice} onSelected={onSelected} />
              ))}
            </div>
          </Flex>
        );
      })}
    </Flex>
  );
};

const SelectItem: React.FC<{
  choice: TypeSelectItem;
  onSelected: (type: TypeSelectItem) => void;
}> = ({ choice, onSelected }) => {
  return (
    <Flex
      className="survey-type-select__card"
      column
      hAlign="center"
      onClick={() => onSelected(choice)}
    >
      <span className="survey-type-select__card-icon">
        <PollyIcon icon={choice.icon} size="large" ignoreTheme inheritColor />
      </span>

      <Text content={choice.label} align="center" className="survey-type-select__card-label" />
    </Flex>
  );
};
